import { MARGIN_CONTENT } from '@/constants/margin.constant';
import { PAGINATION } from '@/constants/pagination.constant';
import dayjs from 'dayjs';
import { camelCase } from 'lodash';

export function toBoolean(input: any) {
  if (input === '0' || input === 'false') return false;
  return !!input;
}

export function toNumber(input: any) {
  const val = +input;
  if (isNaN(val)) return null;
  return val;
}

export function concatClasses(
  ...classes: (string | null | undefined | false | 0)[]
) {
  return classes.filter(Boolean).join(' ');
}

export function camelToSnake(str: string) {
  return str
    .replace(/([a-z])([A-Z])/g, '$1_$2') // Insert underscore between lowercase and uppercase letters
    .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2') // Handle cases where uppercase letters are followed by lowercase letters
    .toLowerCase();
}

export function snakeToCamel(str: string) {
  return str
    .toLowerCase()
    .replace(/([-_][a-z0-9])/g, (group) =>
      group.toUpperCase().replace('-', '').replace('_', '')
    );
}

export function camelObject<O extends Record<string, any>>(
  obj: O
): Record<string, any> {
  if (Array.isArray(obj)) {
    return obj.map(camelObject);
  } else if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        camelCase(key),
        camelObject(value)
      ])
    );
  }
  return obj;
}

export function snakeObject<O extends Record<string, any>>(
  obj: O
): Record<string, any> {
  return Object.keys(obj).reduce<Record<string, any>>((newObj, key) => {
    const isObject =
      obj[key] && typeof obj[key] === 'object' && !Array.isArray(obj[key]);

    newObj[camelToSnake(key)] = isObject ? camelToSnake(obj[key]) : obj[key];
    return newObj;
  }, {});
}

export function delay(ms?: number) {
  if (ms == null) {
    return new Promise((_) => {
      // Never resolve
    });
  }
  return new Promise((res) => setTimeout(res, ms));
}

export function objectToQueryString(query: Record<string, any>, prefix = true) {
  const list = Object.keys(query).reduce((arr: any[], k) => {
    if (query[k] !== undefined) {
      arr.push(`${k}=${query[k]}`);
    }
    return arr;
  }, []);

  return list.length ? `${prefix ? '?' : ''}${list.join('&')}` : '';
}

export const getCssVariableValue = (variable: string) => {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(variable)
    .trim();
};

export const capitalizeFirstLetter = (str: string): string => {
  if (typeof str !== 'string' || str.length === 0) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const getFirstPathnameSegment = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);
  return segments[0] || '';
};

export function undefinedRefine(obj: Record<string, any>) {
  return JSON.parse(JSON.stringify(obj)); //remove key has undefined value
}

export function formatDateTime(date: string) {
  return dayjs(date).format('DD/MM/YYYY HH:mm:ss');
}

export const tableScrollY = (page: number) => {
  return page > PAGINATION.limitPage
    ? window.innerHeight - MARGIN_CONTENT
    : undefined;
};

export const formatDateToDDMMYYYY = (date: string | Date | number) => {
  if (!date) return '';
  const formattedDate = dayjs(date).isValid()
    ? dayjs(date).format('DD/MM/YYYY')
    : '';
  return formattedDate;
};

export const getPathnameSegment = (pathname: string, index: number) => {
  const segments = pathname.split('/').filter(Boolean);
  return segments[index] || '';
};

export const cleanFalsyValues = (values: Record<string, any> = {}) => {
  return Object.fromEntries(
    Object.entries(values).filter(([_, value]) => Boolean(value))
  );
};

export function getKeysByValue<T extends Record<string, any>>(
  obj: T,
  value: any
): string[] {
  return Object.entries(obj)
    .filter(([_, val]) => val === value)
    .map(([key]) => key);
}

export function convertFileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });
}

export function formatDateToHHmmDDMMYYYY(date: string | Date | number) {
  if (!date) return '';
  const formattedDate = dayjs(date).isValid()
    ? dayjs(date).format('HH:mm DD/MM/YYYY')
    : '';
  return formattedDate;
}

export function handleDownload(url: string, fileName: string = 'downloaded-file') {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export const handlePositiveNumberInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (
    !/[0-9.]/.test(e.key) &&
    !['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'].includes(e.key)
  ) {
    e.preventDefault();
  }

  if (e.key === '.' && e.currentTarget.value.includes('.')) {
    e.preventDefault();
  }
  if ((e.key === '.' && e.currentTarget.value.length === 0) || e.key === ' ') {
    e.preventDefault();
  }
};

export const handlePositiveIntegerInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (
    !/[0-9]/.test(e.key) &&
    !['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'].includes(e.key)
  ) {
    e.preventDefault();
  }
  if (e.key === '.' || e.key === ',' || e.key === ' ') {
    e.preventDefault();
  }
};

export function removeVietnameseTones(str: string) {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D');
}

export function handleDecimalInputChange(
  e: React.ChangeEvent<HTMLInputElement>,
  max: number | undefined,
  setEditedRows: any,
  record: any,
  keyName: string
) {
  let val = e.target.value;
  if (!/^\d*(\.\d*)?$/.test(val)) return;
  if (val.startsWith('-')) val = val.slice(1);
  if (val !== '' && Number(val) > (max ?? Infinity)) val = String(max);
  setEditedRows((prev: any) => ({
    ...prev,
    [record.id]: {
      ...prev[record.id],
      [keyName]: val
    }
  }));
}
